/**
 * Custom ReactFlow node renderers per trace-graph node kind.
 *
 * Each kind gets its own colour + icon so an operator can scan the
 * canvas and pick out "the message", "the executions", "the deliveries"
 * at a glance. The anchor node is rendered with a stronger ring so
 * it's obvious where the trace started from.
 */
import {
  Activity,
  Boxes,
  ChevronDown,
  ChevronRight,
  Inbox,
  MessageSquare,
  Send,
  type LucideIcon,
} from "lucide-react";
import { Handle, Position } from "reactflow";

import { cn } from "@/lib/cn";
import type { TraceNodeKind } from "@/lib/api-trace";

export const TRACE_NODE_WIDTH = 240;
export const TRACE_NODE_HEIGHT = 84;

interface KindStyle {
  icon: LucideIcon;
  /** Tailwind border tone applied to the card. */
  border: string;
  /** Tailwind text + icon tone for the kind label. */
  accent: string;
}

const KIND_STYLES: Record<TraceNodeKind, KindStyle> = {
  ingress_source: {
    icon: Inbox,
    border: "border-success/40",
    accent: "text-success",
  },
  message: {
    icon: MessageSquare,
    border: "border-info/40",
    accent: "text-info",
  },
  execution: {
    icon: Activity,
    border: "border-accent/60",
    accent: "text-accent",
  },
  node_execution: {
    icon: Boxes,
    border: "border-muted-foreground/30",
    accent: "text-muted-foreground",
  },
  delivery: {
    icon: Send,
    border: "border-warning/40",
    accent: "text-warning",
  },
};

const KIND_LABEL: Record<TraceNodeKind, string> = {
  ingress_source: "Ingress",
  message: "Message",
  execution: "Execution",
  node_execution: "Node",
  delivery: "Delivery",
};

export interface TraceNodeCardData {
  kind: TraceNodeKind;
  label: string;
  /** Free-form short status hint (e.g. "succeeded", "failed"). */
  status?: string;
  isAnchor: boolean;
  isSelected: boolean;
}

function TraceNodeCard({ data }: { data: TraceNodeCardData }) {
  const style = KIND_STYLES[data.kind];
  const Icon = style.icon;
  // Status tone: failed wins (red ring), succeeded gets a subtle green
  // accent on the status pill, everything else is neutral.
  const failed = data.status === "failed";
  const succeeded = data.status === "succeeded";
  return (
    <div
      className={cn(
        "relative flex flex-col gap-1 overflow-hidden rounded-lg border bg-elevated p-2.5 shadow-sm transition-all",
        style.border,
        "hover:border-accent hover:shadow-md",
        data.isAnchor && "ring-2 ring-accent ring-offset-1 ring-offset-background",
        data.isSelected && !data.isAnchor && "ring-1 ring-accent/60",
        failed && "ring-1 ring-danger/60",
      )}
      style={{ width: TRACE_NODE_WIDTH, height: TRACE_NODE_HEIGHT }}
    >
      {/* Single in/out handle per side — trace graphs are sparse
          enough that multi-slot fan-out is overkill. */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: "transparent", border: "none", width: 6, height: 6, left: -2 }}
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: "transparent", border: "none", width: 6, height: 6, right: -2 }}
        isConnectable={false}
      />
      <div className="flex items-center gap-2">
        <Icon className={cn("h-3.5 w-3.5 shrink-0", style.accent)} strokeWidth={1.75} />
        <span className={cn("text-[10px] font-medium uppercase tracking-wide", style.accent)}>
          {KIND_LABEL[data.kind]}
        </span>
        {data.status && (
          <span
            className={cn(
              "ml-auto rounded px-1.5 py-px text-[10px] font-medium",
              failed && "bg-danger/15 text-danger",
              succeeded && "bg-success/15 text-success",
              !failed && !succeeded && "bg-muted text-muted-foreground",
            )}
          >
            {data.status}
          </span>
        )}
      </div>
      <div className="truncate text-sm font-medium text-foreground" title={data.label}>
        {data.label}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Group container — two variants:
//   "execution": one execution + its node_executions in a single box
//                labelled with the workflow name (default for any
//                execution that has children visible in the trace)
//   "workflow":  ≥2 sibling executions of the same workflow (retry /
//                fanout case), labelled with the workflow name + ×N
// ---------------------------------------------------------------------------

export interface TraceGroupCardData {
  label: string;
  /** Optional small badge to the right of the title — count of
   *  children, multiplier, etc. */
  badge?: string;
  /** "execution" / "workflow" — affects header tone. */
  variant: "execution" | "workflow";
  /** Status of the execution (only meaningful for variant="execution");
   *  drives the header color so a failed run stands out. */
  status?: string;
  /** Whether the box is currently collapsed (children hidden). When
   *  true the chevron points right, when false it points down. */
  collapsed: boolean;
  /** Called when the user clicks anywhere in the header. Toggles the
   *  collapsed state in the parent canvas. */
  onToggle: () => void;
}

function TraceGroupCard({ data }: { data: TraceGroupCardData }) {
  const failed = data.status === "failed";
  const succeeded = data.status === "succeeded";
  const Chevron = data.collapsed ? ChevronRight : ChevronDown;
  return (
    <div
      className={cn(
        "relative h-full w-full overflow-hidden rounded-xl transition-colors",
        // Collapsed → reads as a proper standalone card (solid border,
        // saturated background, shadow). Expanded → reads as a quiet
        // container (dashed border, subtle background) so the visual
        // weight belongs to the children inside.
        data.collapsed
          ? "border bg-elevated shadow-sm"
          : "border-2 border-dashed bg-accent/[0.03]",
        failed
          ? "border-danger/60 hover:border-danger/80"
          : succeeded
            ? "border-success/50 hover:border-success/70"
            : "border-accent/50 hover:border-accent/70",
      )}
    >
      {/* Invisible handles on left/right so ReactFlow can attach
          re-routed edges when the box is collapsed (children hidden,
          but external edges still terminate here). Without these
          ReactFlow silently drops the edges. */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: "transparent", border: "none", width: 6, height: 6, left: -2 }}
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: "transparent", border: "none", width: 6, height: 6, right: -2 }}
        isConnectable={false}
      />
      {/* Whole header is clickable — keep the click-target generous. */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          data.onToggle();
        }}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-2.5 text-xs font-semibold transition-colors",
          "hover:bg-accent/10 active:bg-accent/15",
          failed
            ? "text-danger"
            : succeeded
              ? "text-success"
              : "text-accent",
        )}
      >
        <Chevron className="h-4 w-4 shrink-0 opacity-80" strokeWidth={2} />
        <Activity className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
        <span className="truncate text-left">{data.label}</span>
        {data.badge && (
          <span
            className={cn(
              "ml-auto shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px]",
              failed
                ? "bg-danger/15"
                : succeeded
                  ? "bg-success/15"
                  : "bg-accent/15",
            )}
          >
            {data.badge}
          </span>
        )}
      </button>
    </div>
  );
}

export const TRACE_NODE_TYPES = { trace: TraceNodeCard, traceGroup: TraceGroupCard };
